import React from 'react';
import { connect } from 'react-redux';
import { Box } from '@mui/material';

import { State as RootState } from '@occc-app/rootRedux';

import { actions, State } from './redux';
import {
  ConstraintType,
  LocationInStateConfiguration,
  RequiresActivationConfiguration,
} from './components';

import { State as EarningMechanismState, EarningMechanismKeys } from '../EarningMechanisms/redux';

type EMSelectedConstraints = {
  requiresActivation: boolean;
  locationInState: boolean;
};

const emTypesConstraintOptions = (constraintType: EarningMechanismKeys): EMSelectedConstraints => {
  switch (constraintType) {
    case 'externalFulfillment':
      return { requiresActivation: true, locationInState: true };
    case 'loyaltyEvent':
      return { requiresActivation: true, locationInState: false };
    case 'membershipEvent':
      return { requiresActivation: false, locationInState: false };
    case 'perGroupPurchased':
      return { requiresActivation: true, locationInState: true };
    case 'perItemPurchased':
      return { requiresActivation: true, locationInState: true };
    case 'perItemsAccumulated':
      return { requiresActivation: true, locationInState: true };
    case 'perTaggedGroupPurchased':
      return { requiresActivation: true, locationInState: true };
    case 'perTaggedItemPurchased':
      return { requiresActivation: true, locationInState: true };
    case 'perTaggedItemsAccumulated':
      return { requiresActivation: true, locationInState: true };
    case 'productPurchase':
      return { requiresActivation: true, locationInState: true };
    case 'totalEligiblePurchase':
      return { requiresActivation: true, locationInState: true };
    case 'totalEligiblePurchaseOverTime':
      return { requiresActivation: true, locationInState: true };
    case 'totalSpendOnTransaction':
      return { requiresActivation: true, locationInState: true };
    case 'totalSpendOverTime':
      return { requiresActivation: true, locationInState: true };
    case 'varietyPurchase':
      return { requiresActivation: true, locationInState: true };
    case 'varietyPurchaseOverTime':
      return { requiresActivation: true, locationInState: true };
    case 'visits':
      return { requiresActivation: true, locationInState: true };
  }
};

type Actions = typeof actions;
type ComponentState = State & Pick<EarningMechanismState, 'selectedEarningMechanism'>;
type Props = ComponentState & Actions;

const falseFn = () => false;

class CreateConstraints extends React.Component<Props, {}> {
  renderLocationInState = () => {
    const {
      selectedEarningMechanism,
      locationInState,
      constraintToggleUpdate,
      constraintConfigurationUpdate,
    } = this.props;

    if (
      selectedEarningMechanism === null ||
      !emTypesConstraintOptions(selectedEarningMechanism).locationInState
    ) {
      return null;
    }

    const isActive = locationInState && locationInState.active;

    return (
      <ConstraintType
        constraintToggleUpdate={constraintToggleUpdate}
        key='locationInState'
        type='locationInState'
        name='location'
        description='Require transaction to originate from a location within a set of states'
        isActive={isActive}
      >
        <LocationInStateConfiguration
          {...locationInState}
          constraintConfigurationUpdate={constraintConfigurationUpdate}
          fieldDisabled={falseFn}
        />
      </ConstraintType>
    );
  };

  renderRequiresActivation = () => {
    const {
      selectedEarningMechanism,
      requiresActivation,
      constraintToggleUpdate,
      constraintConfigurationUpdate,
    } = this.props;

    if (
      selectedEarningMechanism === null ||
      !emTypesConstraintOptions(selectedEarningMechanism).locationInState
    ) {
      return null;
    }

    const isActive = requiresActivation && requiresActivation.active;

    return (
      <ConstraintType
        constraintToggleUpdate={constraintToggleUpdate}
        key='requiresActivation'
        type='requiresActivation'
        name='activation'
        description='Require members to activate the offer before engaging with it'
        isActive={isActive}
      >
        <RequiresActivationConfiguration
          {...requiresActivation}
          constraintConfigurationUpdate={constraintConfigurationUpdate}
          fieldDisabled={falseFn}
        />
      </ConstraintType>
    );
  };

  render() {
    return (
      <Box>
        {this.renderLocationInState()}
        {this.renderRequiresActivation()}
      </Box>
    );
  }
}

type MapStateToProps = (state: RootState) => ComponentState;
const mapStateToProps: MapStateToProps = ({
  offer: {
    earningMechanisms: { selectedEarningMechanism },
    constraints,
  },
}) => ({ ...constraints, selectedEarningMechanism });

export default connect(mapStateToProps, actions)(CreateConstraints);
